import React from 'react';
import Linkify from 'linkify-react';
import { isBefore } from 'date-fns';
import { useParams } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { skipToken } from '@reduxjs/toolkit/query';

import { DEFAULT_MINUTES_TO_REOPEN_KIOSK } from 'modules/kiosk/constants';
import { INACTIVITY_TIME_ON_FINAL_INSTRUCTIONS } from 'modules/auth/constants';

import { SelfServiceOrigin, SelfServiceType } from 'modules/selfServices/types/SelfService';

import useInactivity from 'hooks/useInactivity';
import { useDispatch, useSelector } from 'hooks';
import useLabelTranslation from 'hooks/useLabelTranslation';
import selfServicesApi, { useGetSelfServiceByIdQuery, useGetFinalInstructionsQuery, useUpdateSelfServiceMutation } from 'modules/selfServices/service';

import { logout } from 'modules/auth/actions';
import { reopenKiosk } from 'modules/kiosk/actions';
import { addNotification } from 'modules/notifications/actions';

import {
  getOrigin,
  isPublicDevice,
  isRemoteKiosk,
} from 'modules/dealers/selectors';
import {
  canReopenKiosk,
  getParkingInfo,
  getSelfServiceType,
  hasClickableInstruction,
  getUntilDateToReopenKiosk,
} from 'modules/selfServices/selectors';
import { getSelectedSelfServiceId } from 'modules/auth/selectors';
import { getVehicleCheckType } from 'modules/vehicleCheck/selectors';
import { getUnreadCountText, haveUnreadMessages } from 'modules/chat/selectors';
import { isReopenKioskLoading as isReopenKioskLoadingSelector } from 'modules/kiosk/selectors';

import Icon from 'components/ui/Icon';
import Modal from 'components/ui/Modal';
import Loader from 'components/ui/Spinner';
import ChatModal from 'components/ChatModal';
import Carousel from 'components/ui/Carousel';
import Attachments from 'components/ui/Attachments';
import Content from 'components/VehicleCheckStep/Content';
import Button, { ButtonType } from 'components/ui/Button';
import FileDownloadModal from 'components/ui/FileDownloadModal';
import { UPDATE_FIXED_CACHE_KEY } from 'modules/selfServices/constants';
import { NotificationType } from 'modules/notifications/types/Notification';
import CourtesyVehicleModal from 'components/CourtesyVehicle/CourtesyVehicleModal';
import { FinalInstructionsResponse } from 'modules/dealers/types/FinalInstructionsResponse';

import ParkingContent from '../Parking/ParkingContent';
import useDownloadFinalInstructionsDocuments from './useDownloadFinalInstructionsDocuments';

const FinalInstructions: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { getLabelTranslation } = useLabelTranslation();
  const { selfServiceId: selfServiceIdParams } = useParams();

  const origin = useSelector(getOrigin);
  const parking = useSelector(getParkingInfo);
  const isRemote = useSelector(isRemoteKiosk);
  const type = useSelector(getSelfServiceType);
  const isPublic = useSelector(isPublicDevice);
  const unreadCount = useSelector(getUnreadCountText);
  const hasUnreadCount = useSelector(haveUnreadMessages);
  const displayReopenKiosk = useSelector(canReopenKiosk);
  const vehicleCheckType = useSelector(getVehicleCheckType);
  const hasClickableDescription = useSelector(hasClickableInstruction);
  const untilDateToReopenKiosk = useSelector(getUntilDateToReopenKiosk);
  const isReopenKioskLoading = useSelector(isReopenKioskLoadingSelector);
  const selfServiceId = useSelector(getSelectedSelfServiceId) ?? selfServiceIdParams;

  const [isChatModalOpen, setChatModal] = React.useState(false);
  const [isParkingModalOpen, setParkingModal] = React.useState(false);
  const [isCourtesyModalOpen, setCourtesyModal] = React.useState(false);
  const [isDownloadModalOpen, setDownloadModalOpen] = React.useState(false);
  const [isCarouselFullscreen, setCarouselFullscreen] = React.useState(false);
  const [isVehicleCheckModalOpen, setIsVehicleCheckModelOpen] = React.useState(false);

  const { data } = useGetSelfServiceByIdQuery(selfServiceId ? { id: selfServiceId } : skipToken);
  const { data: instructions, isLoading, isSuccess } = useGetFinalInstructionsQuery({ selfServiceId });
  const { documentList, handleDownload, isDownloading } = useDownloadFinalInstructionsDocuments({ selfServiceId });
  const [, { data: updatedSelfService }] = useUpdateSelfServiceMutation({ fixedCacheKey: UPDATE_FIXED_CACHE_KEY });

  const isCheckout = type === SelfServiceType.CHECK_OUT;
  const isCheckin = type === SelfServiceType.CHECK_IN || vehicleCheckType === SelfServiceType.CHECK_IN;

  const displayDownloadButtons = (!isPublic || isRemote) && (!isCheckout || documentList.length > 0);
  const displayDownloadDocuments = displayDownloadButtons && documentList.length > 1;
  const displayDownloadSingleDocument = displayDownloadButtons && documentList.length === 1;

  const hasAcceptedMobility = updatedSelfService?.mobility?.customerAccepted ?? data?.mobility?.customerAccepted;

  const displayCourtesy = isCheckin && Boolean(data?.mobility?.vehicle && hasAcceptedMobility);
  const displayFinalInvoice = !isPublic && isCheckout && data?.finalInvoice?.medias?.length > 0;
  const displayChat = instructions?.isChatEnabled && (origin === SelfServiceOrigin.HOME || isRemote || !isPublic);
  const displayParkingLocation = Boolean(parking?.spotNumber || parking?.x >= 0 || parking?.y >= 0) && isCheckout;

  const Instruction = React.useMemo(
    () => (hasClickableDescription ? Linkify : React.Fragment),
    [hasClickableDescription],
  );

  const instructionProps = React.useMemo( // This avoids giving `options` to a Fragment, which causes a warning
    () => (hasClickableDescription ? { options: { target: { url: '_blank' } } } : {}),
    [hasClickableDescription],
  );

  let finalInstructionMessage;
  if (isSuccess && instructions?.message) {
    finalInstructionMessage = getLabelTranslation(instructions.message);
  }

  const handleShowChat = React.useCallback(() => setChatModal(true), []);
  const handleShowParking = React.useCallback(() => setParkingModal(true), []);
  const handleShowCourtesy = React.useCallback(() => setCourtesyModal(true), []);
  const handleShowDownload = React.useCallback(() => setDownloadModalOpen(true), []);
  const handleCarouselOpen = React.useCallback(() => setCarouselFullscreen(true), []);
  const handleCarouselClose = React.useCallback(() => setCarouselFullscreen(false), []);
  const handleShowVehicleCheck = React.useCallback(() => setIsVehicleCheckModelOpen(true), []);

  const handleCancelVehicleCheck = React.useCallback(async () => {
    await dispatch(selfServicesApi.util.updateQueryData('getFinalInstructions', { selfServiceId }, (instructions: FinalInstructionsResponse) => {
      instructions.courtesyVehicleCheckAccepted = false;
      instructions.vehicleCheckAccepted = false;
      instructions.vehicleCheckLink = undefined;
      instructions.vehicleCheckLinkExpiredAt = undefined;
    }
    ));
    setIsVehicleCheckModelOpen(false);
  }, []);

  const handleReopen = React.useCallback(() => {
    if (!untilDateToReopenKiosk || isBefore(Date.now(), untilDateToReopenKiosk)) {
      dispatch(reopenKiosk());
    } else {
      dispatch(
        addNotification({
          title: {
            id: 'page.finalInstructions.forbiddenReopen.title',
            defaultMessage: 'The kiosk cannot be reopened anymore',
          },
          description: {
            id: 'page.finalInstructions.forbiddenReopen.description',
            defaultMessage: '{minutes} minutes have passed since you completed the self-service.',
            values: {
              minutes: DEFAULT_MINUTES_TO_REOPEN_KIOSK,
            },
          },
          type: NotificationType.ERROR,
        }),
      );
    }
  }, [dispatch, untilDateToReopenKiosk]);

  const handleInactivity = React.useCallback(() => {
    if (isPublic) {
      dispatch(logout());
    }
  }, [dispatch, isPublic]);

  useInactivity({
    // Setting seconds to 0 will avoid a rerender per second when not needed
    seconds: isPublic ? INACTIVITY_TIME_ON_FINAL_INSTRUCTIONS : 0,
    onInactive: handleInactivity,
  });

  const buttons = [
    displayParkingLocation && {
      testId: 'showParking',
      onClick: handleShowParking,
      label: (
        <span className="truncate flex-1 text-left !text-button-s">
          <FormattedMessage id="page.finalInstructions.parking" defaultMessage="Show my parking location" />
        </span>
      ),
      icon: <Icon className="fb-icon--parking-car !leading-none !text-button-s" />,
      addon: (
        <Modal open={isParkingModalOpen} onClose={setParkingModal} className="modal-large">
          <div className="content pb-0">
            <ParkingContent hideInstructions maps={data?.parkingMap?.maps} {...data?.parking} />
          </div>
        </Modal>
      ),
    },
    displayCourtesy && {
      testId: 'showCourtesy',
      onClick: handleShowCourtesy,
      label: (
        <span className="truncate flex-1 text-left !text-button-s">
          <FormattedMessage id="courtesyVehicle.title" defaultMessage="Courtesy vehicle" />
        </span>
      ),
      icon: <Icon className="fb-icon--info !leading-none !text-button-s" />,
      addon: <CourtesyVehicleModal open={isCourtesyModalOpen} onClose={setCourtesyModal} />,
    },
    displayChat && {
      testId: 'showChat',
      onClick: handleShowChat,
      icon: <Icon className="fb-icon--ext-chat !leading-none !text-button-s" />,
      label: (
        <div className="flex-1 overflow-hidden">
          <div className="items-center justify-between gap-2 flex flex-wrap">
            <span className="truncate flex-1 text-left text-default !text-button-s">
              <FormattedMessage id="page.finalInstructions.chat" defaultMessage="Chat with the dealership" />
            </span>
            {hasUnreadCount && (
              <div className="rounded-full p-1.5 bg-brand-default">
                <span
                  className="min-w-4 flex leading-4 items-center text-white justify-center !text-button-s"
                  aria-label={
                    intl.formatMessage({ id: 'accessibility.button.newMessages', defaultMessage: '{count, select, 1 {1 new message} other {{count} new messages}}' },
                      { count: unreadCount }
                    )}
                >
                  {unreadCount}
                </span>
              </div>
            )}
          </div>
        </div>
      ),
      addon: (
        <ChatModal
          open={isChatModalOpen}
          onClose={setChatModal}
          selfServiceId={selfServiceId}
          maxMessageLength={instructions.maxMessageLength}
        />
      ),
    },
    displayDownloadSingleDocument && {
      loading: isDownloading,
      disabled: isDownloading,
      testId: 'downloadDocument',
      onClick: () => handleDownload(documentList[0]),
      icon: <Icon className="fb-icon--download !leading-none !text-button-s"/>,
      label: (
        <span className="truncate flex-1 text-left !text-button-s">
          {documentList[0].filename}
        </span>
      ),
    },
    displayDownloadDocuments && {
      testId: 'downloadDocuments',
      onClick: handleShowDownload,
      icon: <Icon className="fb-icon--work-list !leading-none !text-button-s" />,
      label: (
        <span className="truncate flex-1 text-left !text-button-s">
          <FormattedMessage id="page.finalInstructions.downloadDocuments" defaultMessage="Download documents" />
        </span>
      ),
      addon: (
        <FileDownloadModal
          files={documentList}
          open={isDownloadModalOpen}
          onDownload={handleDownload}
          onClose={setDownloadModalOpen}
        />
      ),
    },
    displayReopenKiosk && {
      onClick: handleReopen,
      testId: 'reopenKiosk',
      loading: isReopenKioskLoading,
      disabled: isReopenKioskLoading,
      icon: <Icon className="fb-icon--doors !leading-none !text-button-s" />,
      label: (
        <span className="truncate flex-1 text-left !text-button-s">
          <FormattedMessage id="page.finalInstructions.reopen" defaultMessage="Reopen the kiosk" />
        </span>
      ),
    },
    instructions?.vehicleCheckLink && {
      onClick: handleShowVehicleCheck,
      testId: 'vehicleCheckInfo',
      icon: <Icon className="fb-icon--self-service-vehicleCheck !leading-none !text-button-s" />,
      label: (
        <span className="truncate flex-1 text-left !text-button-s">
          <FormattedMessage id="page.finalInstructions.vehicleCheckInfo" defaultMessage="Vehicle check information" />
        </span>
      ),
      addon: (
        <Modal
          open={isVehicleCheckModalOpen}
          onClose={setIsVehicleCheckModelOpen}
          className="modal-large"
          title={
            <FormattedMessage
              id="page.vehicleCheck.qrCodeStep.title"
              defaultMessage="Guided vehicle check"
              tagName="h2"
            />
          }
        >
          <div className="flex flex-col items-center justify-center gap-4 kiosk:gap-8 text-center w-full">
            <Content
              onClose={handleCancelVehicleCheck}
              {...instructions}
            />
          </div>
        </Modal>
      ),
    }
  ].filter(Boolean);

  return (
    <>
      {isLoading && <Loader className="homePage" />}
      {isSuccess && (
        <div className="flex flex-col items-center">
          <div className="main-content kiosk:pt-12 pt-5">
            <div className="flex flex-col text-center w-full mb-3">
              <h1 className="text-default">
                <FormattedMessage
                  id="page.finalInstructions.title"
                  defaultMessage="Thank you"
                />
              </h1>
              <p className="w-full text-low mb-3 mt-3 whitespace-pre-wrap break-words">
                <Instruction {...instructionProps}>
                  {finalInstructionMessage}
                </Instruction>
              </p>
            </div>
            <div className="flex gap-4 mb-4 kiosk:mb-8 flex-col rounded-card overflow-hidden w-full">
              {instructions?.medias?.length > 0 && (
                <Carousel
                  className="rounded-xl"
                  medias={instructions.medias}
                  onClose={handleCarouselClose}
                  fullscreen={isCarouselFullscreen}
                  onMediaClick={handleCarouselOpen}
                />
              )}
            </div>
            <div className="flex flex-col gap-4 kiosk:gap-8 w-full">
              {buttons.length > 0 && (
                <div className="flex flex-col gap-2 kiosk:gap-4 items-center p-s w-full rounded-button-list bg-card-bg">
                  {buttons.map(({
                    label, icon, addon, ...rest
                  }, index) => (
                    <React.Fragment key={index}>
                      <Button
                        type={ButtonType.WHITE}
                        className="flex items-center !p-3xs !pl-s justify-between w-full"
                        {...rest}
                      >
                        {label}
                        {icon && (
                          <div className="inline-block !leading-none !text-button-s p-3 kiosk:p-6 rounded-full bg-btn-primary-icon-bg ml-2">
                            {icon}
                          </div>
                        )}
                      </Button>
                      {addon}
                    </React.Fragment>
                  ))}
                </div>
              )}
              {displayFinalInvoice && (
                <div className="flex flex-col gap-2 kiosk:gap-4 items-center p-3 kiosk:p-6 w-full rounded-button-list bg-card-bg">
                  <FormattedMessage
                    tagName="h2"
                    id="page.finalInstructions.documents"
                    defaultMessage="Final invoice documents"
                  />
                  <Attachments
                    documents={data.finalInvoice.medias}
                    className="w-full"
                    buttonProps={{
                      className: 'w-5/6 max-w-lg mb-5',
                      type: ButtonType.TERTIARY,
                    }}
                    singleDownloadTitle={(
                      <FormattedMessage
                        id="page.finalInvoice.buttons.download"
                        defaultMessage="Download invoice"
                      />
                    )}
                  />
                </div>
              )}
              {isPublic && (
                <div className="w-full mb-5 text-center">
                  <Button
                    onClick={handleInactivity}
                    className="w-full"
                    testId="goHome"
                  >
                    <FormattedMessage id="quit" defaultMessage="Quit" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FinalInstructions;
